import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api, AUTH_REFRESH_TOKEN_STORAGE_KEY } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    const err = new Error(sanitizeBusinessMessage(body?.message, 'Authentication failed'))
    err.code = body?.error?.code
    throw err
  }
  return body.data
}

function apiError(err, opts?: { loginAttempt?: boolean }) {
  return toUserFacingApiError(err, opts?.loginAttempt ? 'Login failed' : 'Request failed', opts)
}

/**
 * POST /auth/login — returns JWT for super_admin or tenant users.
 */
export async function login({ email, password }) {
  try {
    const { data } = await api.post('/auth/login', {
      email: email?.trim(),
      password,
    })
    const payload = assertSuccess(data)
    const u = payload.user
    const user = {
      id: String(u.id),
      email: u.email,
      name: u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
      role: u.role,
      adminScope: u.adminScope ?? (u.role === 'super_admin' ? 'ALL' : u.role === 'platform_admin' ? 'ALL' : undefined),
      organisationId: u.organisationId,
    }
    return {
      user,
      token: payload.accessToken,
      refreshToken: typeof payload.refreshToken === 'string' ? payload.refreshToken : undefined,
    }
  } catch (e) {
    throw apiError(e, { loginAttempt: true })
  }
}

export async function changePassword({ currentPassword, newPassword }) {
  try {
    const { data } = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

export async function requestPasswordReset(email: string) {
  try {
    const { data } = await api.post(
      '/auth/forgot-password',
      { email: email?.trim() },
      { skipAuthRefresh: true },
    )
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

export async function resetPassword({
  email,
  code,
  newPassword,
}: {
  email: string
  code: string
  newPassword: string
}) {
  try {
    const { data } = await api.post(
      '/auth/reset-password',
      { email: email?.trim(), code: code?.trim(), newPassword },
      { skipAuthRefresh: true },
    )
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

/**
 * Best-effort server logout.
 *
 * When a refresh token is stored, calls `POST /auth/logout` to revoke it on the API.
 * When only an access token exists (no refresh token in storage), this is a no-op on
 * the server — callers must still clear local session state (`AuthContext.logout`).
 * Network failures are ignored; local sign-out should proceed regardless.
 */
export async function logout(refreshTokenOverride?: string | null) {
  const refresh =
    refreshTokenOverride !== undefined
      ? refreshTokenOverride
      : localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
  if (!refresh) return
  try {
    await api.post('/auth/logout', { refreshToken: refresh }, { skipAuthRefresh: true })
  } catch {
    // best-effort revoke
  }
}
